import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import ToastViewport from '../components/common/ToastViewport.jsx';
import {
  TOAST_DURATION_MS,
  TOAST_EVENTS,
  TOAST_VARIANTS,
  interpolateMessage
} from '../constants/toastEvents.js';

const ToastContext = createContext(null);

const EXIT_ANIMATION_MS = 320;
const MAX_TOASTS = 5;

function resolveToastInput(eventOrOptions, params = {}) {
  if (typeof eventOrOptions === 'string') {
    const def = TOAST_EVENTS[eventOrOptions];
    if (def) {
      return {
        message: interpolateMessage(def.message, params),
        variant: def.variant
      };
    }
    return {
      message: interpolateMessage(eventOrOptions, params),
      variant: params.variant || TOAST_VARIANTS.INFO
    };
  }

  if (eventOrOptions && typeof eventOrOptions === 'object') {
    const { eventId, message, variant, params: nestedParams = {} } = eventOrOptions;
    const mergedParams = { ...nestedParams, ...params };
    if (eventId && TOAST_EVENTS[eventId]) {
      const def = TOAST_EVENTS[eventId];
      return {
        message: message || interpolateMessage(def.message, mergedParams),
        variant: variant || def.variant
      };
    }
    return {
      message: message || '',
      variant: variant || TOAST_VARIANTS.INFO
    };
  }

  return { message: '', variant: TOAST_VARIANTS.INFO };
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());
  const exitTimersRef = useRef(new Map());

  const clearToastTimers = useCallback((id) => {
    const autoTimer = timersRef.current.get(id);
    if (autoTimer) {
      clearTimeout(autoTimer);
      timersRef.current.delete(id);
    }
    const exitTimer = exitTimersRef.current.get(id);
    if (exitTimer) {
      clearTimeout(exitTimer);
      exitTimersRef.current.delete(id);
    }
  }, []);

  const removeToast = useCallback((id) => {
    clearToastTimers(id);
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, [clearToastTimers]);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => {
      const target = prev.find((t) => t.id === id);
      if (!target || target.exiting) return prev;
      return prev.map((t) => (t.id === id ? { ...t, exiting: true } : t));
    });

    clearToastTimers(id);
    const exitTimer = setTimeout(() => removeToast(id), EXIT_ANIMATION_MS);
    exitTimersRef.current.set(id, exitTimer);
  }, [clearToastTimers, removeToast]);

  const showToast = useCallback((eventOrOptions, params = {}) => {
    const { message, variant } = resolveToastInput(eventOrOptions, params);
    if (!message) return null;

    const id = crypto.randomUUID();
    setToasts((prev) => {
      const next = [...prev, { id, message, variant, exiting: false }];
      if (next.length > MAX_TOASTS) {
        const overflow = next.length - MAX_TOASTS;
        next.slice(0, overflow).forEach((t) => clearToastTimers(t.id));
        return next.slice(overflow);
      }
      return next;
    });

    const autoTimer = setTimeout(() => dismissToast(id), TOAST_DURATION_MS);
    timersRef.current.set(id, autoTimer);
    return id;
  }, [clearToastTimers, dismissToast]);

  useEffect(() => () => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    exitTimersRef.current.forEach((timer) => clearTimeout(timer));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}
